// import React, { useState } from 'react';
// import { Box, Typography, Button, Alert, Chip } from '@mui/material';
// import ContentCopyIcon from '@mui/icons-material/ContentCopy';
// import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
// import TaskAltIcon from '@mui/icons-material/TaskAlt';

// interface Props {
//     cctm_id: string;
//     isComplete: boolean;
//     onComplete: () => void;
// }

// const SNIPPET = `<script>
//   cctag('event', 'purchase', {
//     transaction_id: 'YOUR-ORDER-ID',
//     currency: 'INR',
//     value: 499.00,
//     items: [{
//       item_id: 'YOUR-SKU-001',
//       item_name: 'Product Name',
//       price: 499.00,
//       quantity: 1
//     }]
//   });
// </script>`;

// export const Step4Purchase: React.FC<Props> = ({ isComplete, onComplete }) => {
//     const [copied, setCopied] = useState(false);
//     const [completing, setCompleting] = useState(false);

//     const handleCopy = () => {
//         navigator.clipboard.writeText(SNIPPET);
//         setCopied(true);
//         setTimeout(() => setCopied(false), 2500);
//     };

//     const handleComplete = async () => {
//         setCompleting(true);
//         await onComplete();
//         setCompleting(false);
//     };

//     return (
//         <Box>
//             <Box display="flex" alignItems="center" gap={1.5} mb={1}>
//                 <Typography variant="h6" fontWeight={600}>
//                     Step 4 — Track Purchase
//                 </Typography>
//                 {isComplete && (
//                     <Chip
//                         icon={<TaskAltIcon />}
//                         label="Pixel Active"
//                         color="success"
//                         size="small"
//                         variant="outlined"
//                     />
//                 )}
//             </Box>

//             {isComplete && (
//                 <Alert severity="success" sx={{ mb: 2 }}>
//                     Your pixel is active. All affiliate-driven events are being tracked.
//                 </Alert>
//             )}

//             <Typography variant="body2" color="text.secondary" mb={1}>
//                 Add this code on your <strong>Order Confirmation / Thank You page</strong>.
//             </Typography>
//             <Typography variant="body2" color="text.secondary" mb={3}>
//                 <code>transaction_id</code> is your unique order ID — prevents the same order from being counted twice.
//             </Typography>

//             <Box
//                 component="pre"
//                 sx={{
//                     bgcolor: '#1e1e1e',
//                     color: '#d4d4d4',
//                     borderRadius: 1.5,
//                     p: 2,
//                     fontSize: '0.75rem',
//                     overflowX: 'auto',
//                     whiteSpace: 'pre',
//                     fontFamily: 'monospace',
//                     mb: 3,
//                 }}
//             >
//                 {SNIPPET}
//             </Box>

//             <Box display="flex" gap={2} flexWrap="wrap">
//                 <Button
//                     variant="outlined"
//                     startIcon={copied ? <CheckCircleOutlineIcon color="success" /> : <ContentCopyIcon />}
//                     onClick={handleCopy}
//                     sx={{ textTransform: 'none' }}
//                 >
//                     {copied ? 'Copied!' : 'Copy Code'}
//                 </Button>
//                 {!isComplete && (
//                     <Button
//                         variant="contained"
//                         color="success"
//                         onClick={handleComplete}
//                         disabled={completing}
//                         sx={{ textTransform: 'none' }}
//                     >
//                         Complete Setup ✓
//                     </Button>
//                 )}
//             </Box>
//         </Box>
//     );
// };


import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Chip,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
// import CodeSnippet from '../components/CodeSnippet';
import PlatformSnippetTabs from '../components/PlatformSnippetTabs';
// import { purchaseSnippets } from './platformSnippets';
import { purchaseOptions } from './platformSnippets';

interface Props {
    cctm_id: string;
    pixel?: any;
    platforms?: string[];
    isComplete: boolean;
    onComplete: () => void;
    onBack: () => void;
}

// const SNIPPET = `<script>
//   cctag('event', 'purchase', {
//     transaction_id: 'YOUR-ORDER-ID',
//     currency: 'INR',
//     value: 499.00,
//     items: [{
//       item_id: 'YOUR-SKU-001',
//       item_name: 'Product Name',
//       price: 499.00,
//       quantity: 1
//     }]
//   });
// </script>`;

export const Step4Purchase: React.FC<Props> = ({
    pixel,
    platforms,
    isComplete,
    onComplete,
    onBack
}) => {
    // const [copied, setCopied] = useState(false);
    const [completing, setCompleting] = useState(false);

    // const handleCopy = () => {
    //     navigator.clipboard.writeText(SNIPPET);
    //     setCopied(true);
    //     setTimeout(() => setCopied(false), 2500);
    // };

    const handleComplete = async () => {
        setCompleting(true);
        await onComplete();
        setCompleting(false);
    };

    return (
        <Box>
            {/* Header */}
            <Box mb={2}>
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1.5}
                    mb={1}
                >
                    <Typography
                        variant="h6"
                        fontWeight={700}
                    >
                        Step 4 — Track Purchase
                    </Typography>

                    {isComplete && (
                        <Chip
                            icon={<TaskAltIcon />}
                            label="Pixel Active"
                            color="success"
                            size="small"
                            variant="outlined"
                        />
                    )}
                </Box>

                <Typography
                    color="text.secondary"
                    sx={{
                        maxWidth: 700,
                        fontSize: '0.875rem',
                    }}
                >
                    Fire this event on your order confirmation
                    or thank you page whenever a purchase is completed.
                </Typography>
            </Box>

            {/* Success Card */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2.5,
                    borderRadius: 4,
                    bgcolor: '#ECFDF5',
                    border: '1px solid #BBF7D0',
                    mb: 2,
                }}
            >
                <CheckCircleOutlineIcon
                    sx={{
                        color: '#22C55E',
                        fontSize: 28,
                    }}
                />

                <Box>
                    <Typography fontWeight={700}>
                        Purchase Tracking
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        Track completed orders and attribute
                        revenue to affiliate campaigns.
                    </Typography>
                </Box>
            </Box>

            {/* Active State */}
            {isComplete && (
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 3,
                        bgcolor: '#F0FDF4',
                        border: '1px solid #BBF7D0',
                        mb: 3,
                    }}
                >
                    <Typography
                        variant="body2"
                        color="#166534"
                        fontWeight={600}
                    >
                        ✓ Your pixel is active and tracking
                        affiliate-driven purchases.
                    </Typography>
                </Box>
            )}

            {/* Instructions */}
            <Typography
                variant="subtitle1"
                fontWeight={700}
                mb={1}
            >
                Event Code
            </Typography>

            <Typography
                variant="body2"
                color="text.secondary"
                mb={2}
            >
                Add this on your
                <strong> Order Confirmation / Thank You Page</strong>.
                The <code>transaction_id</code> should be unique
                for every order to prevent duplicate tracking.
            </Typography>

            {/* Code Block */}
            {/* <CodeSnippet
                title="purchase.js"
                code={SNIPPET}
                copied={copied}
                onCopy={handleCopy}
            /> */}
            {/* <PlatformSnippetTabs
                platforms={platforms}
                snippets={purchaseSnippets(pixel)}
            /> */}
            <PlatformSnippetTabs options={purchaseOptions(pixel)} />

            {/* Actions */}
            {!isComplete && (
                 <Box
                                display="flex"
                                gap={2}
                                flexWrap="wrap"
                            >
                <button
                    onClick={() => onBack?.()}
                    className='btn btn-outline-primary btn-sm'
                >
                    ← Back
                </button>
                <Button
                    variant="contained"
                    onClick={handleComplete}
                    disabled={completing}
                    sx={{
                        borderRadius: 999,
                        px: 4,
                        py: 1.2,
                        fontWeight: 700,
                        textTransform: 'none',
                        background:
                            'linear-gradient(135deg,#22C55E,#16A34A)',
                        transition: '.25s',

                        '&:hover': {
                            transform: 'translateY(-2px)',
                            background:
                                'linear-gradient(135deg,#22C55E,#16A34A)',
                        },
                    }}
                >
                    {completing
                        ? 'Activating...'
                        : 'Complete Setup ✓'}
                </Button>
                </Box>
            )}
        </Box>
    );
};